import { useQuery, useQueryClient } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import { CheckCircle2, History, ReceiptText, RefreshCw, Wallet } from 'lucide-react'
import { useState, type ReactNode } from 'react'
import { toast } from 'sonner'

import {
  purchasesService,
  type AccountsPayablePurchase,
  type AccountsPayableResult,
  type CreateAccountsPayablePaymentPayload,
} from '../../services/purchases.service'
import { useSessionStore } from '../../store/auth/auth.store'
import { GlobalFilterBar } from '../ui/GlobalFilterBar'
import { SearchFilter } from '../ui/SearchFilter'
import { Button } from '../ui/button'
import { DiscreteLoader } from '../ui/discrete-loader'
import { GlobalPaginator } from '../ui/global-paginator'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { TabsContent } from '../ui/tabs'

import { formatPayableDate, formatPayableMoney } from './accounts-payable'
import { AddPurchasePaymentModal } from './modals/AddPurchasePaymentModal'
import { PurchasePaymentHistoryModal } from './modals/PurchasePaymentHistoryModal'

const perPage = 5

const emptyResult: AccountsPayableResult = {
  purchases: { current_page: 1, data: [], from: null, to: null, per_page: perPage, total: 0 },
  totals: { total_amount: '0.00', balance_amount: '0.00', paid_amount: '0.00', pending_count: 0 },
  filters: { sucursales: [], suppliers: [] },
}

function FilterField({ label, htmlFor, children }: { label: string; htmlFor: string; children: ReactNode }) {
  return (
    <div className="space-y-2">
      <label htmlFor={htmlFor} className="text-sm font-medium text-slate-700">{label}</label>
      {children}
    </div>
  )
}

export function AccountsPayableTab({ active = true }: { active?: boolean }) {
  const queryClient = useQueryClient()
  const canCreatePayment = useSessionStore((state) => state.canAction('purchases.account_payable.create'))
  const [search, setSearch] = useState('')
  const [branch, setBranch] = useState('all')
  const [supplier, setSupplier] = useState('all')
  const [status, setStatus] = useState<'pending' | 'all'>('pending')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [page, setPage] = useState(1)
  const [selectedPurchase, setSelectedPurchase] = useState<AccountsPayablePurchase | null>(null)
  const [historyPurchase, setHistoryPurchase] = useState<AccountsPayablePurchase | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const hasFilters = Boolean(search || branch !== 'all' || supplier !== 'all' || status !== 'pending' || dateFrom || dateTo)
  const accountsQuery = useQuery({
    queryKey: ['purchase-accounts-payable', { search, branch, supplier, status, dateFrom, dateTo, page }],
    queryFn: () => purchasesService.getAccountsPayable({
      search: search || undefined,
      sucursal_id: branch === 'all' ? undefined : Number(branch),
      supplier_id: supplier === 'all' ? undefined : Number(supplier),
      status,
      date_from: dateFrom || undefined,
      date_to: dateTo || undefined,
      page,
      per_page: perPage,
    }),
    enabled: active,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  })
  const result = accountsQuery.data?.data ?? emptyResult
  const summary = [
    { label: 'Saldo pendiente', value: formatPayableMoney(result.totals.balance_amount), icon: Wallet },
    { label: 'Abonado en estas compras', value: formatPayableMoney(result.totals.paid_amount), icon: CheckCircle2 },
    { label: 'Compras pendientes', value: String(result.totals.pending_count), icon: ReceiptText },
  ]

  const clearFilters = () => {
    setSearch('')
    setBranch('all')
    setSupplier('all')
    setStatus('pending')
    setDateFrom('')
    setDateTo('')
    setPage(1)
  }

  const registerPayment = async (payment: CreateAccountsPayablePaymentPayload) => {
    if (!selectedPurchase) return
    setIsSubmitting(true)
    try {
      const response = await purchasesService.createAccountsPayablePayment(selectedPurchase.id, payment)
      toast.success(response.message)
      setSelectedPurchase(null)
      await queryClient.invalidateQueries({ queryKey: ['purchase-accounts-payable'] })
      await queryClient.invalidateQueries({ queryKey: ['purchase-account-payments', selectedPurchase.id] })
    } catch (error) {
      const message = isAxiosError<{ message?: string }>(error) ? error.response?.data?.message : undefined
      toast.error(message || (error instanceof Error ? error.message : 'No fue posible registrar el abono.'))
      await queryClient.invalidateQueries({ queryKey: ['purchase-accounts-payable'] })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <TabsContent value="accounts-payable" className="space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-xl font-semibold text-header-foreground">Cuentas por pagar</h2>
          </div>
          <p className="mt-1 text-sm text-card-description">Consulta compras a crédito con saldo pendiente.</p>
        </div>
      </div>

      <GlobalFilterBar
        show={['dates']}
        values={{ startDate: dateFrom, endDate: dateTo }}
        onChange={(changes) => {
          if (changes.startDate !== undefined) setDateFrom(changes.startDate)
          if (changes.endDate !== undefined) setDateTo(changes.endDate)
          setPage(1)
        }}
        onClearFilters={clearFilters}
        hasActiveFilters={hasFilters}
      >
        <FilterField label="Buscar compra" htmlFor="payables-search">
          <SearchFilter
            id="payables-search"
            initialValue={search}
            placeholder="Folio o proveedor..."
            onSearch={(value) => { setSearch(value); setPage(1) }}
          />
        </FilterField>
        <FilterField label="Sucursal" htmlFor="payables-branch">
          <Select value={branch} onValueChange={(value) => { setBranch(value); setPage(1) }}>
            <SelectTrigger id="payables-branch" aria-label="Sucursal"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Todas las sucursales</SelectItem>{result.filters.sucursales.map((item) => <SelectItem key={item.id} value={String(item.id)}>{item.name}</SelectItem>)}</SelectContent>
          </Select>
        </FilterField>
        <FilterField label="Proveedor" htmlFor="payables-supplier">
          <Select value={supplier} onValueChange={(value) => { setSupplier(value); setPage(1) }}>
            <SelectTrigger id="payables-supplier" aria-label="Proveedor"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Todos los proveedores</SelectItem>{result.filters.suppliers.map((item) => <SelectItem key={item.id} value={String(item.id)}>{item.name}</SelectItem>)}</SelectContent>
          </Select>
        </FilterField>
        <FilterField label="Estado" htmlFor="payables-status">
          <Select value={status} onValueChange={(value) => { setStatus(value as 'pending' | 'all'); setPage(1) }}>
            <SelectTrigger id="payables-status" aria-label="Estado"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">Pendientes</SelectItem>
              <SelectItem value="all">Todas</SelectItem>
            </SelectContent>
          </Select>
        </FilterField>
      </GlobalFilterBar>

      <div>
        <dl className="grid gap-3 sm:grid-cols-3">
          {summary.map(({ label, value, icon: Icon }) => (
            <div key={label} className="flex items-center justify-between gap-3 rounded-lg border border-border bg-card p-4">
              <div><dt className="text-sm text-card-description">{label}</dt><dd className="mt-1 text-2xl font-semibold tabular-nums text-card-foreground">{value}</dd></div>
              <div className="rounded-lg bg-primary/10 p-2.5 text-primary"><Icon className="h-5 w-5" /></div>
            </div>
          ))}
        </dl>
      </div>

      <div className="overflow-hidden rounded-lg border border-table-border bg-table-bg">
        {accountsQuery.isLoading ? (
          <div className="min-h-56"><DiscreteLoader text="Cargando cuentas por pagar..." /></div>
        ) : accountsQuery.isError ? (
          <div className="flex min-h-56 flex-col items-center justify-center px-6 py-10 text-center">
            <p className="font-medium text-table-foreground">No fue posible cargar las cuentas por pagar</p>
            <Button type="button" variant="outline" className="mt-4" onClick={() => void accountsQuery.refetch()}><RefreshCw className="mr-2 h-4 w-4" /> Reintentar</Button>
          </div>
        ) : result.purchases.data.length === 0 ? (
          <div className="flex min-h-56 flex-col items-center justify-center px-6 py-10 text-center">
            <ReceiptText className="mb-3 h-9 w-9 text-card-description" />
            <p className="font-medium text-table-foreground">
              {status === 'all' ? 'No hay compras a crédito para mostrar' : 'No hay cuentas pendientes para mostrar'}
            </p>
            <p className="mt-1 text-sm text-card-description">
              {hasFilters
                ? 'No hay compras que coincidan con los filtros seleccionados.'
                : 'Todas las compras están liquidadas.'}
            </p>
            {hasFilters && <Button type="button" variant="outline" className="mt-4" onClick={clearFilters}>Quitar filtros</Button>}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1060px] text-sm">
                <caption className="sr-only">Cuentas por pagar</caption>
                <thead className="border-b border-table-border bg-table-header-bg text-left text-xs uppercase text-table-header-foreground">
                  <tr>{['Folio', 'Proveedor', 'Sucursal', 'Fecha compra', 'Total', 'Abonado', 'Saldo pendiente', 'Acciones'].map((heading, index) => <th key={heading} scope="col" className={`px-4 py-3 font-medium ${index >= 4 && index <= 6 ? 'text-right' : ''}`}>{heading}</th>)}</tr>
                </thead>
                <tbody className="divide-y divide-table-border">
                  {result.purchases.data.map((purchase) => (
                    <tr key={purchase.id} className="text-table-foreground transition-colors hover:bg-table-row-hover">
                      <td className="px-4 py-4 font-medium">{purchase.reference_number}</td>
                      <td className="max-w-52 px-4 py-4">{purchase.supplier.name}</td>
                      <td className="px-4 py-4">{purchase.sucursal.name}</td>
                      <td className="whitespace-nowrap px-4 py-4">{formatPayableDate(purchase.purchase_date)}</td>
                      <td className="px-4 py-4 text-right tabular-nums">{formatPayableMoney(purchase.total_amount, purchase.currency)}</td>
                      <td className="px-4 py-4 text-right tabular-nums text-card-description">{formatPayableMoney(purchase.paid_amount, purchase.currency)}</td>
                      <td className="px-4 py-4 text-right font-semibold tabular-nums">{formatPayableMoney(purchase.balance_amount, purchase.currency)}</td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-1">
                          {canCreatePayment && Number(purchase.balance_amount) > 0 && (
                            <button
                              type="button"
                              aria-label={`Agregar abono a ${purchase.reference_number}`}
                              title="Agregar abono"
                              onClick={() => setSelectedPurchase(purchase)}
                              className="rounded p-2 text-primary"
                            >
                              <Wallet className="h-4 w-4" />
                            </button>
                          )}
                          <button
                            type="button"
                            aria-label={`Ver historial de abonos de ${purchase.reference_number}`}
                            title="Historial de abonos"
                            onClick={() => setHistoryPurchase(purchase)}
                            className="rounded p-2 text-slate-600"
                          >
                            <History className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <GlobalPaginator totalItems={result.purchases.total} itemsPerPage={result.purchases.per_page} currentPage={result.purchases.current_page} onPageChange={setPage} from={result.purchases.from ?? undefined} to={result.purchases.to ?? undefined} />
          </>
        )}
      </div>

      {selectedPurchase && <AddPurchasePaymentModal key={selectedPurchase.id} purchase={selectedPurchase} onClose={() => setSelectedPurchase(null)} onRegister={registerPayment} isSubmitting={isSubmitting} />}
      {historyPurchase && <PurchasePaymentHistoryModal key={historyPurchase.id} purchase={historyPurchase} onClose={() => setHistoryPurchase(null)} />}
    </TabsContent>
  )
}
